import React, { useRef, useState, useEffect, useCallback } from "react";
import Button, { BUTTON_DISPLAY, BUTTON_SIZE } from "@acadia-ui/button";
import BookFiltersStore from "../../Flux/Stores/BookFiltersStore";
import PlacementDataStore from "../../Flux/Stores/PlacementDataStore";
import PackagesDataStore from "../../Flux/Stores/PackagesDataStore";
import { toggleWorkbookBudgetLocked, toggleTrafficManager } from "./../../Flux/Actions/WorkbookActions";
import Modal from "@acadia-ui/modal";
import LockBudgetConfirmationModal from "../Modals/LockBudgetConfirmationModal";
import Loading from "@acadia-ui/loading";

import ExportOptions from "./features/ExportOptions";

import PropTypes from "prop-types";
import { showToast, TOAST_TYPE, ToastNotification } from "@acadia-ui/toast";

// hide the view jira button as per ACTMASTER-4854
const ACTMASTER_4854 = true;

NavigationActions.propTypes = {
	masterCampaignID: PropTypes.string,
	metaData: PropTypes.object,
	availableActivations: PropTypes.array,
	onUpdate: PropTypes.func,
	templateExists: PropTypes.number,
	fetchMetaData: PropTypes.func,
	campaignJiraTicketLink: PropTypes.string
};

export default function NavigationActions({
	// this comment exists to prevent eslint from making this unreadable.
	metaData,
	templateExists,
	fetchMetaData,
	selectedAuditLevel,
	campaignJiraTicketLink
}) {
	const lockBudgetRef = useRef();
	const [prismaLocked, setPrismaLocked] = useState(null); // one of: [null, true, false]. null means not prisma campaign.
	const [enableTMToggle, setEnableTMToggle] = useState(false);
	const [trafficManagerEnabled, setTrafficManagerEnabled] = useState(null);
	const [disableTrafficManagerToggle, setDisableTrafficManagerToggle] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [TMModalOpen, setTMModalOpen] = useState(false);

	useEffect(() => {
		if (metaData?.PlatformID == 5) {
			const onLoad = () => {
				setPrismaLocked(PlacementDataStore?.getData?.()?.some((datum) => datum?.IsPrismaBudgetLocked == "True"));
				setDisableTrafficManagerToggle(
					metaData?.IsTrafficManagerEnabled &&
						PlacementDataStore?.getData?.()?.some((datum) => datum?.TraffickingPlacementStatusID && ["2", "3", "4", "5"].includes(datum?.TraffickingPlacementStatusID))
				);
			};

			PackagesDataStore.on("packages_loaded", onLoad);
			PlacementDataStore.on("placements_loaded", onLoad);
			setEnableTMToggle(metaData?.IsTrafficManagerButtonEnabled);
			setTrafficManagerEnabled(metaData?.IsTrafficManagerEnabled);
			setDisableTrafficManagerToggle(
				metaData?.IsTrafficManagerEnabled &&
					PlacementDataStore?.getData?.()?.some((datum) => datum?.TraffickingPlacementStatusID && ["2", "3", "4", "5"].includes(datum?.TraffickingPlacementStatusID))
			);

			return () => {
				PackagesDataStore.removeListener("packages_loaded", onLoad);
				PlacementDataStore.removeListener("placements_loaded", onLoad);
			};
		}
		setPrismaLocked(null);
	}, [metaData]);

	const handleLockUnlock = useCallback(
		(e) => {
			lockBudgetRef.current.triggerOpenClose(e, !prismaLocked);
		},
		[prismaLocked]
	);

	const changeLockBudgetStatus = useCallback(() => {
		try {
			toggleWorkbookBudgetLocked(metaData.CampaignUniqueIDCode, !prismaLocked)
				.then(() => {
					setPrismaLocked(!prismaLocked);
				})
				.then(() => {
					showToast(
						"workbookToasts",
						<ToastNotification type={TOAST_TYPE.SUCCESS} message={`Budget is Successfully ${prismaLocked ? "unlocked" : "Locked"}`} timed={5000} />
					);
				})
				.catch((err) => {
					showToast("workbookToasts", <ToastNotification type={TOAST_TYPE.ERROR} message={`Budget was not successfully ${prismaLocked ? "unlocked" : "locked"}`} />);
				});
		} catch (e) {
			showToast("workbookToasts", <ToastNotification type={TOAST_TYPE.ERROR} message={`Budget was not successfully locked or unlocked`} />);
		}
	}, [metaData, prismaLocked]);

	const onToggleTM = () => {
		setIsLoading(true);
		toggleTrafficManager(!trafficManagerEnabled, metaData?.CampaignUniqueIDCode)
			.then(() => {
				fetchMetaData();
			})
			.then(() => {
				showToast(
					"workbookToasts",
					<ToastNotification
						type={TOAST_TYPE.SUCCESS}
						message={`The Traffic Manager columns have been successfully ${
							trafficManagerEnabled ? "disabled" : "enabled"
						}. You can now use this information for trafficking`}
						timed={5000}
					/>
				);
			})
			.catch((err) => {
				err?.response?.data?.Errors.forEach((error) => {
					showToast("workbookToasts", <ToastNotification type={TOAST_TYPE.ERROR} message={error} />);
				});
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	return (
		<div className="workbook-navigation-actions">
			{campaignJiraTicketLink && !ACTMASTER_4854 && (
				<Button
					// this comment exists to prevent eslint from making this unreadable.
					display={BUTTON_DISPLAY.TEXT}
					icon={"icon-external-link"}
					href={campaignJiraTicketLink}
					target="_blank"
					data-tracking-id-alt="view-jira-ticket"
				>
					View Jira Ticket
				</Button>
			)}

			<ExportOptions auditLevelID={selectedAuditLevel?.AuditLevelID} templateExists={templateExists} metaData={metaData || {}}>
				{prismaLocked !== null && (
					<Button
						// this comment exists to prevent eslint from making this unreadable.
						display={BUTTON_DISPLAY.TEXT}
						data-tracking-id-alt="lock-unlock-budget"
						size={BUTTON_SIZE.SMALL}
						icon={`icon-${prismaLocked ? "unlock" : "lock"}`}
						disabled={metaData?.ExternalSystemStatusID !== 3 || selectedAuditLevel?.AuditLevelID == null}
						onClick={handleLockUnlock}
					>
						{prismaLocked ? "Unlock" : "Lock"} Budget
					</Button>
				)}
			</ExportOptions>

			{enableTMToggle && prismaLocked !== null && (
				<Button
					// this comment exists to prevent eslint from making this unreadable.
					display={BUTTON_DISPLAY.SECONDARY}
					data-tracking-id-alt="toggle-traffic-manager"
					onClick={() => setTMModalOpen(true)}
					disabled={disableTrafficManagerToggle || selectedAuditLevel?.AuditLevelID == null}
				>
					{`${trafficManagerEnabled ? "Disable" : "Enable"} Traffic Manager`}
				</Button>
			)}

			<LockBudgetConfirmationModal
				lockBudgetModal={(ref) => {
					lockBudgetRef.current = ref;
				}}
				action="Cancel"
				onSubmit={changeLockBudgetStatus}
			/>

			<Modal
				id="traffic__manager"
				heading={!trafficManagerEnabled ? "Enable Traffic Manager Columns" : "Disable Traffic Manager Columns"}
				description={`Please make sure all work is saved before ${
					!trafficManagerEnabled ? "enabling" : "disabling"
				} Traffic Manager columns; otherwise, you may lose unsaved information`}
				isOpen={TMModalOpen}
				onRequestClose={() => setTMModalOpen(false)}
				userClosable={false}
				actionSetProps={{
					actionsRight: (
						<>
							<Button display="secondary" onClick={() => setTMModalOpen(false)} data-tracking-id-alt="cancel-traffic-manager-toggle">
								Cancel
							</Button>
							<Button
								display="primary"
								onClick={() => {
									setTMModalOpen(false);
									onToggleTM();
								}}
								data-tracking-id-alt="confirm-traffic-manager-toggle"
							>
								{!trafficManagerEnabled ? "Enable" : "Disable"}
							</Button>
						</>
					)
				}}
				onClick={() => {}}
			>
				<div style={{ height: "50px" }}></div>
				{isLoading && <Loading />}
			</Modal>
		</div>
	);
}
